import { useState } from 'react';
import { toast } from 'react-toastify';
import { Alert, Button, Pagination } from '@shared/components';
import { PermissionGuard } from '@/features/auth';
import { useFranchisees, useAttachFranchiseMutation, useDetachFranchiseMutation } from '../hooks/useFranchises';
import { FranchiseMembersTable } from './FranchiseMembersTable';
import { FranchisePartnerSelect } from './FranchisePartnerSelect';
import { isApiError } from '@/services/api';
import type { Dealer } from '../types/dealers.types';

interface Props {
    dealerId: string;
    dealerName: string;
}

/**
 * Franchises tab: this page is an oem; lists the franchise dealers it
 * franchises, with attach (pick a franchise_dealer) and detach. Mirrors
 * OemsTab with the franchise-dealer/oem roles swapped — the picked dealer is
 * always the franchise_dealer endpoint of attach/detach.
 *
 * Layout follows the shared "Add New / Attach" pattern (see DealerContacts):
 * the table sits on top and the attach affordance lives in a panel below it,
 * toggling between an Attach button and an inline picker (no modal).
 */
export function FranchisesTab({ dealerId, dealerName }: Props) {
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(20);
    const [attaching, setAttaching] = useState(false);
    const [pickedDealer, setPickedDealer] = useState<Dealer | null>(null);
    const [attachError, setAttachError] = useState<string | null>(null);
    const [detachingId, setDetachingId] = useState<string | null>(null);

    const { data, isLoading, isError } = useFranchisees(dealerId, page, perPage);
    const attachMutation = useAttachFranchiseMutation();
    const detachMutation = useDetachFranchiseMutation();

    const rows = data?.data ?? [];
    const meta = data?.meta;

    const closeAttach = () => {
        setAttaching(false);
        setPickedDealer(null);
        setAttachError(null);
    };

    const handleAttach = () => {
        if (!pickedDealer) return;
        setAttachError(null);
        attachMutation.mutate(
            { franchiseDealerId: pickedDealer.id, oemId: dealerId },
            {
                onSuccess: () => {
                    toast.success(`${pickedDealer.name} attached to ${dealerName}.`);
                    closeAttach();
                },
                onError: (err: unknown) => {
                    const msg = isApiError(err) ? err.message : 'Failed to attach franchise dealer. Please try again.';
                    setAttachError(msg);
                    toast.error(msg);
                },
            },
        );
    };

    const handleDetach = (franchiseDealerId: string) => {
        const dealer = rows.find((r) => r.id === franchiseDealerId);
        if (!window.confirm(`Detach ${dealer?.name ?? 'this dealer'} from ${dealerName}?`)) return;

        setDetachingId(franchiseDealerId);
        detachMutation.mutate(
            { franchiseDealerId, oemId: dealerId },
            {
                onSuccess: () => {
                    toast.success(`${dealer?.name ?? 'Dealer'} detached.`);
                    setDetachingId(null);
                },
                onError: (err: unknown) => {
                    const msg = isApiError(err) ? err.message : 'Failed to detach franchise dealer. Please try again.';
                    toast.error(msg);
                    setDetachingId(null);
                },
            },
        );
    };

    if (isError) {
        return (
            <Alert variant="error">
                <span>Failed to load franchise dealers.</span>
            </Alert>
        );
    }

    return (
        <div className="space-y-6">
            <FranchiseMembersTable
                data={rows}
                isLoading={isLoading}
                onDetach={handleDetach}
                detachingId={detachingId}
                emptyMessage="No franchise dealers attached to this OEM yet."
            />

            {meta && meta.last_page > 1 && (
                <Pagination
                    currentPage={meta.current_page}
                    lastPage={meta.last_page}
                    total={meta.total}
                    perPage={meta.per_page}
                    onPageChange={setPage}
                    onPerPageChange={(next) => { setPerPage(next); setPage(1); }}
                />
            )}

            <div className="border-t border-mp-border pt-6 max-w-2xl">
                {attaching ? (
                    <div>
                        <h3 className="font-semibold mb-2">Attach a Franchise Dealer to {dealerName}</h3>
                        <FranchisePartnerSelect
                            partnerType="franchise_dealer"
                            value={pickedDealer}
                            onChange={setPickedDealer}
                            excludeIds={rows.map((r) => r.id)}
                            disabled={attachMutation.isPending}
                        />

                        {attachError && <p className="text-mp-error text-xs mt-1">{attachError}</p>}

                        <div className="flex justify-end gap-2 pt-3">
                            <Button type="button" variant="outline" size="sm" onClick={closeAttach} disabled={attachMutation.isPending}>
                                Cancel
                            </Button>
                            <Button
                                type="button"
                                size="sm"
                                onClick={handleAttach}
                                disabled={!pickedDealer || attachMutation.isPending}
                            >
                                {attachMutation.isPending ? 'Attaching...' : 'Attach'}
                            </Button>
                        </div>
                    </div>
                ) : (
                    <div>
                        <h3 className="font-semibold mb-2">Attach a Franchise Dealer</h3>
                        <div className="flex items-start gap-4 justify-between">
                            <p className="text-sm text-mp-muted">
                                Franchise a dealer to {dealerName}. Only live (non-retired) dealers can be attached.
                            </p>
                            <PermissionGuard permission={{ resource: 'dealer', action: 'edit' }}>
                                <Button type="button" size="sm" variant="primary" onClick={() => setAttaching(true)} className="whitespace-nowrap">
                                    Attach Dealer
                                </Button>
                            </PermissionGuard>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
